import { Box, useMediaQuery } from '@mui/material';
import { FiltersToolbar } from '@diana/core';
import { useTheme } from '@mui/system';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFiltersStore } from '../../stores/useFilters';
import { useFilters, createPropertyToFilter } from '../../hooks/useFilters';
import RightDrawer from '../RightDrawer/RightDrawer';
import { useParams } from 'react-router-dom';
import { HubsNames, useSignalRHubStore } from '../../stores/useSignalR';
import { ResetButton } from '../Buttons/ResetButton';
import { ExportButton } from '../Buttons/ExportButton';
import { BunkeringLogsDataGrid } from '../DataGrids/BunkeringLogsDataGrid';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { useTranslation } from 'react-i18next';
import { useMessageStore, registerFiltersStore } from '../../stores/useMessageStore';
import { useExportHandler } from '../../hooks/useExportHandler';
import { ConfirmationModal } from '../Modals/ConfirmationModal';
import { ErrorModal } from '../Modals/ErrorModal';
import { getBunkeringLogsExportFileName } from '../../utils/stringUtils';
import { BUNKERING_LOGS_NS } from './i18n';
import { getDefaultTimeRange } from '../../utils/timeRangeUtils';
import { convertFiltersToQuery } from '../../utils/convertFiltersToQuery';

const BUNKERING_LOGS_STORE_ID = 'bunkering-logs';

export default function BunkeringLogs() {
  const { t } = useTranslation([BUNKERING_LOGS_NS]);
  const dataGridRef = useRef<GridApiPremium>(null);
  const params = useParams();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.up(700));
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);
  const [selectedBMT, setSelectedBMT] = useState(0);
  const [initPage, setInitPage] = useState(true);
  const [selectedRow, setSelectedRow] = useState({});

  const filtersStore = useFiltersStore(BUNKERING_LOGS_STORE_ID, {
    timeRange: getDefaultTimeRange(),
  });

  useEffect(() => {
    registerFiltersStore(BUNKERING_LOGS_STORE_ID, filtersStore);
  }, [filtersStore]);

  const {
    filtersValues,
    availableFilters,
    filters,
    setAvailableFilters,
    setFilters,
    setFiltersValues,
    resetData,
  } = filtersStore;

  const setStreamArgs = useSignalRHubStore((state) => state.setStreamArgs);
  const disposeStream = useSignalRHubStore((state) => state.disposeStream);

  useEffect(() => {
    const query = convertFiltersToQuery(filtersValues);
    if (query) {
      setStreamArgs(HubsNames.deliverySummary, query);
    }

    return () => {
      disposeStream(HubsNames.deliverySummary);
    };
  }, [filtersValues, setStreamArgs, disposeStream]);
  const messages = useMessageStore((state) => state.messages[HubsNames.deliverySummary] || []);
  const loading = useMessageStore((state) => state.stableLoadingFlags[HubsNames.deliverySummary]);

  const { defaultData, onFilterChange } = useFilters({
    createPropertyToFilter,
    filtersValues,
    setFiltersValues,
    availableFilters,
  });

  const {
    showExportModal,
    showErrorModal,
    isExporting,
    setShowExportModal,
    setShowErrorModal,
    handleExportClick,
    handleExportConfirm,
    handleExportRetry,
  } = useExportHandler({
    onExport: async (fileName: string) => {
      dataGridRef.current?.exportDataAsCsv({ fileName });
    },
    getFileName: getBunkeringLogsExportFileName,
  });

  const handleBTNClick = useCallback((row: any) => {
    setIsDrawerOpened(true);
    setSelectedBMT(row.bunkerMeterTicketNumber);
    setSelectedRow(row);
  }, []);

  useEffect(() => {
    if (!isDrawerOpened) {
      setSelectedBMT(0);
    }
  }, [isDrawerOpened]);

  useEffect(() => {
    if (params.deviceIdBmt && initPage) {
      const deviceIdBmt = +params.deviceIdBmt;
      if (!loading && messages.length > 0) {
        const matchingRow = messages.find((row) => row.bunkerMeterTicketNumber === deviceIdBmt);
        if (matchingRow) {
          setSelectedBMT(deviceIdBmt);
          setSelectedRow(matchingRow);
          setIsDrawerOpened(true);
        }
        setInitPage(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.deviceIdBmt, loading, messages]);

  const onResetAllClick = useCallback(() => {
    resetData();
  }, [resetData]);
  const onRowClick = useCallback((args: any) => handleBTNClick(args.row), [handleBTNClick]);

  const toHideStatusColumn = useMemo(() => {
    if (messages.length) {
      const temp = messages.filter((msg) => msg.mpaTransmissionStatus !== null);
      return temp.length === 0;
    }
    return true;
  }, [messages]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        mt: '10px',
        mb: '5px',
      }}
    >
      <Box
        component="section"
        display="flex"
        justifyContent="center"
        sx={{
          '.MuiStack-root': {
            flex: 1,
            minWidth: 0,
          },
        }}
      >
        <FiltersToolbar
          availableFilters={availableFilters.map((filter) =>
            createPropertyToFilter(filter.property, setFiltersValues),
          )}
          defaultData={defaultData}
          filters={filters}
          filtersValues={filtersValues}
          onFilterChange={onFilterChange}
          setFilters={setFilters}
          setAvailableFilters={setAvailableFilters}
          setFiltersValues={setFiltersValues}
          showAddButton={false}
          showResetButton={false}
          showRemoveButton={false}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-evenly',
            marginTop: '0',
            height: isMobile ? '36px' : 'auto',
            paddingBottom: isMobile ? 0 : '10px',
          }}
        >
          <ResetButton isDisabled={true} onResetAllClick={onResetAllClick} />
          <ExportButton onHandleExportData={handleExportClick} disabled={messages.length === 0} />
        </Box>
      </Box>
      <Box className="content">
        <BunkeringLogsDataGrid
          ref={dataGridRef}
          data={messages}
          loading={loading}
          onRowClick={onRowClick}
          hideMPAStatus={toHideStatusColumn}
        />
      </Box>
      <RightDrawer
        data={selectedRow}
        selectedBMT={selectedBMT}
        isDrawerOpened={isDrawerOpened}
        setIsDrawerOpened={setIsDrawerOpened}
      />
      <ConfirmationModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={handleExportConfirm}
        title={t('components/BunkeringLogs:modals.export.title')}
        message={t('components/BunkeringLogs:modals.export.message')}
        confirmText={t('components/BunkeringLogs:modals.export.confirmText')}
        cancelText={t('components/BunkeringLogs:modals.export.cancelText')}
        isLoading={isExporting}
      />
      <ErrorModal
        open={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onRetry={handleExportRetry}
        message={t('components/BunkeringLogs:modals.export.error.message')}
      />
    </Box>
  );
}
